import { useState } from 'react';
import { ArrowUpRight, ArrowDownRight, ChevronDown, Zap } from 'lucide-react';

const fmtPrice = (p) => {
  if (p == null || p === 0) return '-';
  const n = Number(p);
  return n >= 1 ? n.toFixed(2) : n.toFixed(6);
};
const fmtAmt = (n, abs = false) => {
  const v = abs ? Math.abs(Number(n || 0)) : Number(n || 0);
  return v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};
const symbolInitials = (sym) => {
  if (!sym) return '•';
  const s = String(sym).replace(/USDT|USD|INR|BTC$|PERP/g, '').trim();
  return s.slice(0, 3).toUpperCase() || '•';
};
const colorForSymbol = (sym) => {
  const palette = ['from-emerald-500 to-emerald-700', 'from-orange-500 to-orange-700', 'from-sky-500 to-sky-700', 'from-violet-500 to-violet-700', 'from-rose-500 to-rose-700', 'from-amber-500 to-amber-700'];
  let h = 0;
  for (let i = 0; i < (sym || '').length; i++) h = (h * 31 + sym.charCodeAt(i)) & 0xffffffff;
  return palette[Math.abs(h) % palette.length];
};

export const TradeRowCard = ({ trade, index = 0 }) => {
  const [open, setOpen] = useState(false);
  const net = Number(trade.net_pnl || 0);
  const isWin = net > 0;
  const isFlat = net === 0;
  const isFutures = trade.source === 'futures_trade' || trade.source === 'futures_manual';
  const isManual = trade.source === 'futures_manual';
  const direction = (trade.direction || '').toLowerCase();
  const dirLabel = direction === 'long' ? 'LONG' : direction === 'short' ? 'SHORT' : null;
  const typeLabel = isFutures ? (isManual ? 'FUT · M' : 'FUTURES') : (trade.source === 'trade' ? 'SPOT' : 'PNL');
  const typeColor = isFutures ? 'bg-orange-100 text-orange-700' : trade.source === 'trade' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600';

  return (
    <button
      type="button"
      onClick={() => setOpen((v) => !v)}
      className="w-full text-left bg-white border border-brand-border rounded-xl p-3 sm:p-4 hover:border-brand-primary/60 hover:shadow-sm transition-all active:scale-[0.995]"
      data-testid={`trade-card-${trade.id || index}`}
    >
      {/* Header row */}
      <div className="flex items-center gap-3">
        {/* Symbol badge */}
        <div className={`shrink-0 w-11 h-11 rounded-xl bg-gradient-to-br ${colorForSymbol(trade.symbol)} text-white font-bold text-xs flex items-center justify-center shadow-sm`}>
          {symbolInitials(trade.symbol || trade.asset_name)}
        </div>

        {/* Middle */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap">
            <p className="font-heading text-sm font-bold text-brand-text-primary truncate">
              {trade.symbol || trade.asset_name || 'Trade'}
            </p>
            {dirLabel && (
              <span className={`inline-flex items-center gap-0.5 text-[9px] font-semibold px-1.5 py-0.5 rounded-full ${direction === 'long' ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'}`}>
                {direction === 'long' ? <ArrowUpRight size={9} /> : <ArrowDownRight size={9} />}
                {dirLabel}
              </span>
            )}
            {trade.leverage ? (
              <span className="inline-flex items-center gap-0.5 text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-orange-100 text-orange-700">
                <Zap size={9} /> {trade.leverage}x
              </span>
            ) : null}
            <span className={`text-[9px] font-semibold px-1.5 py-0.5 rounded-full ${typeColor}`}>{typeLabel}</span>
          </div>
          <p className="text-[11px] text-brand-text-secondary mt-0.5">
            {trade.date}
            {trade.quantity != null && (
              <>
                <span className="mx-1 opacity-60">·</span>
                Qty {Number(trade.quantity).toLocaleString(undefined, { maximumFractionDigits: 6 })}
              </>
            )}
          </p>
        </div>

        {/* Right — net P&L */}
        <div className="text-right shrink-0">
          <p className={`font-heading text-sm sm:text-base font-bold ${isWin ? 'text-brand-success' : isFlat ? 'text-brand-text-secondary' : 'text-brand-error'}`}>
            {net >= 0 ? '+' : ''}${fmtAmt(net)}
          </p>
          <div className="flex items-center justify-end gap-1 mt-0.5">
            <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full ${isWin ? 'bg-emerald-100 text-emerald-700' : isFlat ? 'bg-slate-100 text-slate-500' : 'bg-rose-100 text-rose-700'}`}>
              {isWin ? 'WIN' : isFlat ? 'FLAT' : 'LOSS'}
            </span>
            <ChevronDown size={12} className={`text-brand-text-secondary transition-transform ${open ? 'rotate-180' : ''}`} />
          </div>
        </div>
      </div>

      {/* Expand drawer */}
      {open && (
        <div className="mt-3 pt-3 border-t border-brand-border grid grid-cols-2 gap-x-4 gap-y-2 text-xs" data-testid={`trade-expand-${trade.id || index}`}>
          <div>
            <p className="text-[10px] uppercase tracking-wider text-brand-text-secondary">Entry Price</p>
            <p className="font-mono font-semibold text-brand-text-primary">${fmtPrice(trade.entry_price)}</p>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-wider text-brand-text-secondary">Exit Price</p>
            <p className="font-mono font-semibold text-brand-text-primary">${fmtPrice(trade.exit_price)}</p>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-wider text-brand-text-secondary">Gross Profit</p>
            <p className="font-semibold text-brand-success">+${fmtAmt(trade.gross_profit)}</p>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-wider text-brand-text-secondary">Loss</p>
            <p className="font-semibold text-brand-error">-${fmtAmt(trade.loss, true)}</p>
          </div>
          {trade.quantity != null && (
            <div>
              <p className="text-[10px] uppercase tracking-wider text-brand-text-secondary">Quantity</p>
              <p className="font-mono text-brand-text-primary">{Number(trade.quantity).toLocaleString(undefined, { maximumFractionDigits: 6 })}</p>
            </div>
          )}
          {trade.leverage ? (
            <div>
              <p className="text-[10px] uppercase tracking-wider text-brand-text-secondary">Leverage</p>
              <p className="font-semibold text-orange-600">{trade.leverage}x</p>
            </div>
          ) : null}
          {trade.notes && (
            <div className="col-span-2">
              <p className="text-[10px] uppercase tracking-wider text-brand-text-secondary">Notes</p>
              <p className="text-brand-text-secondary italic">"{trade.notes}"</p>
            </div>
          )}
        </div>
      )}
    </button>
  );
};

const FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'wins', label: 'Wins' },
  { id: 'losses', label: 'Losses' },
  { id: 'spot', label: 'Spot' },
  { id: 'futures', label: 'Futures' },
];

export const SegmentedTradeFilter = ({ value, onChange, counts = {} }) => (
  <div
    className="flex gap-1.5 overflow-x-auto no-scrollbar pb-1 -mx-1 px-1"
    data-testid="segmented-trade-filter"
  >
    {FILTERS.map((f) => {
      const active = f.id === value;
      return (
        <button
          key={f.id}
          onClick={() => onChange(f.id)}
          className={`shrink-0 h-8 px-3.5 rounded-full text-xs font-semibold transition-all active:scale-95 flex items-center gap-1.5 ${active ? 'bg-brand-primary text-white shadow-sm' : 'bg-white border border-brand-border text-brand-text-secondary hover:border-brand-primary'}`}
          data-testid={`filter-${f.id}`}
        >
          {f.label}
          {counts[f.id] != null && (
            <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-bold ${active ? 'bg-white/20 text-white' : 'bg-brand-bg text-brand-text-secondary'}`}>
              {counts[f.id]}
            </span>
          )}
        </button>
      );
    })}
  </div>
);
